/**
 * Per-market corporate identity blocks (Egypt / Saudi Arabia).
 * Values here OVERRIDE the global profile on market-scoped PDFs
 * (invoices, quotations, credit/debit notes). Empty = global fallback.
 */
import { useEffect, useState } from "react";
import { Card, SectionHeader, Button, TextInput } from "./lib";
import { Field } from "./lib.overlay";
import { api } from "../services/api";

type Block = Record<string, string | number | null | undefined>;
const MARKETS: { id: "eg" | "sa"; label: string; vat: number }[] = [
  { id: "eg", label: "🇪🇬 Egypt · مصر", vat: 14 },
  { id: "sa", label: "🇸🇦 Saudi Arabia · السعودية", vat: 15 },
];
const FIELDS: { key: string; label: string; width?: number }[] = [
  { key: "legalName",   label: "Legal name (EN)", width: 300 },
  { key: "legalNameAr", label: "الاسم القانوني (AR)", width: 300 },
  { key: "address",     label: "Address", width: 380 },
  { key: "phone",       label: "Phone", width: 240 },
  { key: "whatsapp",    label: "WhatsApp", width: 240 },
  { key: "email",       label: "Email", width: 300 },
  { key: "website",     label: "Website", width: 300 },
  { key: "cr",          label: "Commercial Reg. (CR)", width: 240 },
  { key: "taxNumber",   label: "Tax / VAT number", width: 240 },
];

export const MarketBlocksEditor = () => {
  const [blocks, setBlocks] = useState<Record<string, Block>>({});
  const [active, setActive] = useState<"eg" | "sa">("eg");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    api.companyProfile().then((p) => {
      const m = (p.markets as Record<string, Block>) || {};
      setBlocks({ eg: { ...(m.eg || {}) }, sa: { ...(m.sa || {}) } });
    }).catch(() => setBlocks({ eg: {}, sa: {} }));
  }, []);

  const blk = blocks[active] || {};
  const set = (k: string, v: string | number) =>
    setBlocks((cur) => ({ ...cur, [active]: { ...cur[active], [k]: v } }));

  async function save() {
    setBusy(true); setMsg(null);
    try {
      const payload: Record<string, Block> = {};
      for (const m of MARKETS) {
        const b = blocks[m.id] || {};
        const clean: Block = {};
        for (const f of [...FIELDS.map((x) => x.key), "vatPct", "stampDataUrl", "stampMime", "stampName"]) {
          if (f in b) clean[f] = b[f] === "" ? null : b[f];
        }
        if (Object.keys(clean).length) payload[m.id] = clean;
      }
      await api.patchCompanyProfile({ markets: payload });
      setMsg({ ok: true, text: "Saved — market-scoped invoices & PDFs now use these blocks (empty fields fall back to the global profile)." });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card testId="settings-market-blocks">
      <SectionHeader
        eyebrow="Per-market corporate identity"
        title="Market blocks · Egypt / Saudi Arabia"
      />
      <div className="text-xs mb-4" style={{ color: "var(--ph-ink-muted)" }}>
        A Saudi invoice renders the Saudi legal name, CR, tax number and stamp — the Egyptian one renders the Egyptian block. Any empty field falls back to the global profile above.
      </div>
      <div className="flex gap-2 mb-4">
        {MARKETS.map((m) => (
          <button
            key={m.id}
            data-testid={`market-block-tab-${m.id}`}
            onClick={() => setActive(m.id)}
            className="px-3 py-1.5 text-xs font-semibold"
            style={{
              letterSpacing: "0.08em",
              borderRadius: "var(--ph-radius-sm)",
              border: "1px solid var(--ph-border-strong)",
              background: active === m.id ? "var(--ph-gold, #B8965A)" : "transparent",
              color: active === m.id ? "#fff" : "var(--ph-ink-strong)",
            }}
          >{m.label}</button>
        ))}
      </div>
      {FIELDS.map((f) => (
        <Field key={f.key} label={f.label} inline>
          <div style={{ width: f.width || 260 }}>
            <TextInput
              value={String(blk[f.key] ?? "")}
              onChange={(v) => set(f.key, v)}
              testId={`market-block-${active}-${f.key}`}
            />
          </div>
        </Field>
      ))}
      <Field label="VAT %" inline>
        <div style={{ width: 100 }}>
          <TextInput
            value={String(blk.vatPct ?? MARKETS.find((m) => m.id === active)!.vat)}
            onChange={(v) => set("vatPct", Number(v) || 0)}
            testId={`market-block-${active}-vatPct`}
          />
        </div>
      </Field>
      <Field label="Market stamp" hint="Optional — overrides the global stamp on this market's formal documents." inline>
        <div className="flex items-center gap-3">
          {blk.stampDataUrl ? (
            <img src={String(blk.stampDataUrl)} alt="stamp" style={{ maxHeight: 48 }} data-testid={`market-block-${active}-stamp-img`} />
          ) : (
            <span className="text-xs" style={{ color: "var(--ph-ink-muted)" }} data-testid={`market-block-${active}-stamp-none`}>Global stamp</span>
          )}
          <label
            className="px-3 py-1.5 text-xs font-semibold uppercase cursor-pointer"
            style={{ background: "var(--ph-gold, #B8965A)", color: "#fff", letterSpacing: "0.1em", borderRadius: "var(--ph-radius-sm)" }}
            data-testid={`market-block-${active}-stamp-upload`}
          >
            {blk.stampDataUrl ? "Replace" : "Upload"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid={`market-block-${active}-stamp-file`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const r = new FileReader();
                r.onload = () => {
                  setBlocks((cur) => ({
                    ...cur,
                    [active]: { ...cur[active], stampDataUrl: String(r.result), stampName: file.name, stampMime: file.type },
                  }));
                };
                r.readAsDataURL(file);
              }}
            />
          </label>
          {!!blk.stampDataUrl && (
            <button
              className="text-xs underline"
              style={{ color: "var(--ph-danger)" }}
              data-testid={`market-block-${active}-stamp-remove`}
              onClick={() => setBlocks((cur) => ({ ...cur, [active]: { ...cur[active], stampDataUrl: "", stampName: "", stampMime: "" } }))}
            >Remove</button>
          )}
        </div>
      </Field>
      {msg && (
        <div
          data-testid="market-blocks-msg"
          className="mt-3 p-3 text-sm"
          style={{
            background: msg.ok ? "var(--ph-success-tint)" : "var(--ph-danger-tint)",
            color:      msg.ok ? "var(--ph-success)"      : "var(--ph-danger)",
            borderRadius: "var(--ph-radius-sm)",
          }}
        >{msg.text}</div>
      )}
      <div className="pt-4 flex justify-end">
        <Button testId="market-blocks-save" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save market blocks"}
        </Button>
      </div>
    </Card>
  );
};
